import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { storageService, FileUploadProgress, FileWithYear } from '../services/storageService';
import { AlertCircle, CheckCircle, Loader, Upload, FolderOpen } from 'lucide-react';
import toast from 'react-hot-toast';

const FileUploadPage: React.FC = () => {
  const [selectedFiles, setSelectedFiles] = useState<FileWithYear[]>([]);
  const [uploadProgress, setUploadProgress] = useState<FileUploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [folderSelected, setFolderSelected] = useState(false);

  const handleSelectFolder = async () => {
    try {
      toast.loading('Selecting folder...');
      const files = await storageService.selectFolder();

      if (files.length === 0) {
        toast.dismiss();
        toast.error('No .zip files found in the selected folder');
        return;
      }

      setSelectedFiles(files);
      setFolderSelected(true);
      toast.dismiss();
      toast.success(`Found ${files.length} zip file(s)`);
    } catch (err) {
      toast.dismiss();
      const errorMessage = err instanceof Error ? err.message : 'Failed to select folder';
      toast.error(errorMessage);
      console.error('Folder selection error:', err);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('No files selected');
      return;
    }

    setIsUploading(true);
    try {
      await storageService.uploadFilesByYear(selectedFiles, setUploadProgress);
      toast.success('All files uploaded successfully!');
      setSelectedFiles([]);
      setUploadProgress([]);
      setFolderSelected(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      toast.error(errorMessage);
      console.error('Upload error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFiles([]);
    setUploadProgress([]);
    setFolderSelected(false);
  };

  const groupedByYear = new Map<number, FileWithYear[]>();
  for (const file of selectedFiles) {
    if (!groupedByYear.has(file.year)) {
      groupedByYear.set(file.year, []);
    }
    groupedByYear.get(file.year)!.push(file);
  }

  const totalSize = storageService.calculateTotalSize(selectedFiles);

  return (
    <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          Upload Files
        </h1>
        <p className="text-gray-600">
          Select a folder containing zip files to upload organized by year
        </p>
      </div>

      {!folderSelected ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <FolderOpen size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Select a Folder to Upload
          </h3>
          <p className="text-gray-600 mb-6">
            Choose a folder containing .zip files. Files will be automatically organized by year
            extracted from the filename (e.g., 2024_data.zip → 2024 folder).
          </p>
          <Button
            onClick={handleSelectFolder}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <FolderOpen className="mr-2" size={18} />
            Select Folder
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-semibold text-blue-900">Summary</h3>
                <p className="text-sm text-blue-700 mt-1">
                  {selectedFiles.length} file(s) selected • {storageService.formatFileSize(totalSize)}
                </p>
              </div>
            </div>
          </div>

          {Array.from(groupedByYear.entries())
            .sort(([yearA], [yearB]) => yearB - yearA)
            .map(([year, files]) => {
              const yearSize = files.reduce((sum, f) => sum + f.file.size, 0);
              return (
                <div key={year} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{year}</h3>
                    <span className="text-sm text-gray-600">
                      {files.length} file(s) • {storageService.formatFileSize(yearSize)}
                    </span>
                  </div>

                  <div className="space-y-2">
                    {files.map((f, idx) => {
                      const progress = uploadProgress.find(
                        (p) => p.fileName === f.fileName
                      );

                      return (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {f.fileName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {storageService.formatFileSize(f.file.size)}
                            </p>
                          </div>

                          {progress ? (
                            <div className="flex items-center gap-2 ml-4">
                              {progress.status === 'uploading' && (
                                <Loader className="animate-spin text-indigo-600" size={16} />
                              )}
                              {progress.status === 'success' && (
                                <CheckCircle className="text-green-600" size={16} />
                              )}
                              {progress.status === 'error' && (
                                <AlertCircle className="text-red-600" size={16} />
                              )}
                              <span className="text-xs font-medium text-gray-600 min-w-[40px] text-right">
                                {progress.status === 'error' ? 'Error' : `${progress.progress}%`}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

          <div className="flex gap-3">
            <Button
              onClick={handleUpload}
              disabled={isUploading || uploadProgress.some((p) => p.status === 'error')}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {isUploading ? (
                <>
                  <Loader className="mr-2 animate-spin" size={18} />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="mr-2" size={18} />
                  Upload Files
                </>
              )}
            </Button>

            <Button
              onClick={handleReset}
              disabled={isUploading}
              variant="outline"
              className="px-6"
            >
              Reset
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadPage;
